// @ts-check

import { snap } from '../utils/common_constants.js?v=20260706d';
import {
  applyDrawingObjectState,
  canonicalDrawingObjectKey,
  drawingObjectWorldBounds,
  drawingObjectState,
  getDrawingObject,
  getDrawingObjectForOwner
} from '../objects/drawing_object_registry.js?v=20260904f';
import {
  getSelectedDrawingObjects
} from '../selection/drawing_selection_manager.js?v=20260706d';
import { emit } from '../utils/event_bus.js?v=20260706d';
import {
  boundsCenter,
  mergeBounds,
  transformSnapshotBounds as fallbackTransformSnapshotBounds
} from '../2d/geometry/bounds.js?v=20260706d';
import { rotateGroup2D } from '../2d/transforms/rotate.js?v=20260904f';
import { rotateDrawingObjectsWithDefaultAdapter } from '../2d/transforms/rotationAdapters.js?v=20260904f';

function normalizeDelta(delta = {}, options = {}) {
  const gridSize = Number(options.gridSize);
  const shouldSnap = options.snap === true && Number.isFinite(gridSize) && gridSize > 0;
  const dx = Number(delta.dx ?? delta.x) || 0;
  const dz = Number(delta.dz ?? delta.z) || 0;
  return {
    dx: shouldSnap ? snap(dx, gridSize) : dx,
    dz: shouldSnap ? snap(dz, gridSize) : dz
  };
}

function cleanIdentityPart(value) {
  return String(value || '').trim();
}

function objectOwner(object = {}) {
  return cleanIdentityPart(object?.systemType || object?.systemId || object?.owner);
}

function canonicalObject(object = null) {
  const id = cleanIdentityPart(object?.id);
  const owners = declaredOwners(object);
  const owner = owners[0] || '';
  if (!id || owners.length > 1) return null;
  return owner ? getDrawingObjectForOwner(owner, id) : getDrawingObject(id);
}

function canonicalObjects(objects = []) {
  const objectsByKey = new Map();
  for (const candidate of objects || []) {
    const object = canonicalObject(candidate);
    const key = object ? canonicalDrawingObjectKey(object.systemType, object.id) : '';
    if (key) objectsByKey.set(key, object);
  }
  return [...objectsByKey.values()];
}

function selectedOrProvidedObjects(objects = null) {
  return canonicalObjects(Array.isArray(objects) ? objects : getSelectedDrawingObjects());
}

function declaredOwners(value = {}) {
  return [
    value?.systemType,
    value?.systemId,
    value?.systemKey,
    value?.system,
    value?.owner,
    value?.ownerId
  ].map(cleanIdentityPart).filter(Boolean);
}

function snapshotEntryIdentity(entry = {}) {
  try {
    const state = entry?.state || entry;
    const ids = [...new Set([entry?.id, state?.id].map(cleanIdentityPart).filter(Boolean))];
    const owners = [...new Set([
      ...declaredOwners(entry),
      ...declaredOwners(state)
    ])];
    if (ids.length !== 1 || owners.length > 1) return null;
    const id = ids[0];
    const systemType = owners[0] || '';
    const key = systemType ? canonicalDrawingObjectKey(systemType, id) : '';
    if (cleanIdentityPart(entry?.key) && cleanIdentityPart(entry.key) !== key) return null;
    return { id, systemType, key, state };
  } catch (error) {
    console.warn('DrawingObject-Snapshotidentität konnte nicht gelesen werden.', error);
    return null;
  }
}

function resolveSnapshotEntry(entry = {}) {
  const identity = snapshotEntryIdentity(entry);
  if (!identity) return null;
  const object = identity.systemType
    ? getDrawingObjectForOwner(identity.systemType, identity.id)
    : getDrawingObject(identity.id);
  if (!object) return null;
  const systemType = objectOwner(object);
  if (identity.systemType && identity.systemType !== systemType) return null;
  return {
    ...identity,
    key: canonicalDrawingObjectKey(systemType, object.id),
    systemType,
    object
  };
}

function safeApplyDrawingObjectState(object, state, operation) {
  try {
    return applyDrawingObjectState(object, state);
  } catch (error) {
    console.warn(`DrawingObject-${operation} für ${objectOwner(object) || '(ohne Owner)'} fehlgeschlagen.`, error);
    return false;
  }
}

function safeDrawingObjectState(object, operation) {
  try {
    return drawingObjectState(object);
  } catch (error) {
    console.warn(`DrawingObject-${operation} für ${objectOwner(object) || '(ohne Owner)'} konnte nicht gelesen werden.`, error);
    return null;
  }
}

export function transformSnapshotBounds(snapshot = []) {
  return (snapshot || []).reduce((bounds, entry) => {
    const identity = snapshotEntryIdentity(entry);
    if (!identity) return bounds;
    const state = identity.state;
    return mergeBounds(
      bounds,
      drawingObjectWorldBounds(state) || fallbackTransformSnapshotBounds([entry])
    );
  }, null);
}

export function createTransformSnapshot(objects = null) {
  const snapshot = [];
  for (const object of selectedOrProvidedObjects(objects)) {
    const state = safeDrawingObjectState(object, 'Snapshot');
    if (!state) continue;
    snapshot.push({
      id: object.id,
      systemType: object.systemType,
      key: canonicalDrawingObjectKey(object.systemType, object.id),
      state
    });
  }
  return snapshot;
}

export function moveDrawingObjects(objects = null, delta = {}, options = {}) {
  const normalizedDelta = normalizeDelta(delta, options);
  const moved = [];
  for (const object of selectedOrProvidedObjects(objects)) {
    if (!object?.movable) continue;
    const state = safeDrawingObjectState(object, 'Verschiebung');
    if (!state) continue;
    const nextState = {
      ...state,
      position: {
        x: Number(state.position?.x) + normalizedDelta.dx,
        z: Number(state.position?.z) + normalizedDelta.dz
      }
    };
    if (safeApplyDrawingObjectState(object, nextState, 'Verschiebung')) moved.push({ object, state: nextState });
  }
  if (moved.length) emit('object:moved', { objects: moved.map(entry => entry.object), delta: normalizedDelta });
  return moved.length;
}

export function applyTransformSnapshotDelta(snapshot = [], delta = {}, options = {}) {
  const normalizedDelta = normalizeDelta(delta, options);
  const moved = [];
  for (const entry of snapshot || []) {
    const resolved = resolveSnapshotEntry(entry);
    const object = resolved?.object;
    const state = resolved?.state;
    if (!object?.movable || !state?.position) continue;
    const nextState = {
      ...state,
      position: {
        x: Number(state.position.x) + normalizedDelta.dx,
        z: Number(state.position.z) + normalizedDelta.dz
      }
    };
    if (safeApplyDrawingObjectState(object, nextState, 'Snapshot-Verschiebung')) moved.push({ object, state: nextState });
  }
  if (moved.length) emit('object:moved', { objects: moved.map(entry => entry.object), delta: normalizedDelta, snapshot: true });
  return moved.length;
}

export function rotateDrawingObjects(objects = null, angleDeg = 90, options = {}) {
  const candidates = selectedOrProvidedObjects(objects).filter(object => object?.rotatable);
  return rotateGroup2D({
    objects: candidates,
    angleDeg,
    pivot: options.pivot,
    absolute: options.absolute === true,
    exactTransform: options.exactTransform,
    allowSelectionSet: options.group !== false,
    source: options.source || 'drawing-transform-controller'
  });
}

export function applyTransformSnapshotRotation(snapshot = [], angleDeg = 90, options = {}) {
  const rotationDelta = Number(angleDeg) || 0;
  const entries = [];
  for (const entry of snapshot || []) {
    const resolved = resolveSnapshotEntry(entry);
    const object = resolved?.object;
    const state = resolved?.state;
    if (!object?.rotatable || !state?.position) continue;
    entries.push({ object, state, systemType: resolved.systemType, key: resolved.key });
  }
  if (!entries.length) return 0;
  const validatedSnapshot = entries.map(entry => ({
    id: entry.object.id,
    systemType: entry.systemType,
    key: entry.key,
    state: entry.state
  }));
  const pivot = options.pivot || boundsCenter(transformSnapshotBounds(validatedSnapshot));
  if (!pivot) return 0;
  const entriesByOwner = new Map();
  for (const entry of entries) {
    if (!entriesByOwner.has(entry.systemType)) entriesByOwner.set(entry.systemType, []);
    entriesByOwner.get(entry.systemType).push(entry);
  }
  const rotated = [];
  for (const [systemType, ownerEntries] of entriesByOwner.entries()) {
    const objects = ownerEntries.map(entry => entry.object);
    const baseStates = new Map(ownerEntries.flatMap(entry => [
      [entry.object, entry.state],
      [entry.key, entry.state]
    ]));
    const group = {
      groupId: String(options.groupId || objects.map(object => object.id).join('|')),
      objects,
      primaryObject: objects[0],
      systemType,
      systemId: systemType,
      systemKey: systemType,
      system: systemType,
      owner: systemType,
      ownerId: systemType
    };
    try {
      rotated.push(...rotateDrawingObjectsWithDefaultAdapter(group, rotationDelta, pivot, {
        absolute: options.absolute === true,
        exactTransform: true,
        forcePivot: true,
        baseStates
      }));
    } catch (error) {
      for (const entry of ownerEntries) {
        safeApplyDrawingObjectState(entry.object, entry.state, 'Rotations-Rollback');
      }
      console.warn(`DrawingObject-Snapshotrotation für ${systemType} fehlgeschlagen.`, error);
    }
  }
  if (rotated.length) emit('object:rotated', {
    objects: rotated.map(entry => entry.object),
    angleDeg: rotationDelta,
    pivot,
    snapshot: true
  });
  return rotated.length;
}

export function scaleDrawingObjects(objects = null, scaleFactor = 1, options = {}) {
  const factor = Number(scaleFactor);
  if (!Number.isFinite(factor) || factor <= 0) return 0;
  const scaled = [];
  for (const object of selectedOrProvidedObjects(objects)) {
    if (!object?.scalable) continue;
    const state = safeDrawingObjectState(object, 'Skalierung');
    if (!state) continue;
    const currentScale = state.scale && typeof state.scale === 'object'
      ? state.scale
      : { x: Number(state.scale) || 1, y: Number(state.scale) || 1, z: Number(state.scale) || 1 };
    const nextState = {
      ...state,
      scale: {
        x: currentScale.x * factor,
        y: currentScale.y * factor,
        z: currentScale.z * factor
      }
    };
    if (safeApplyDrawingObjectState(object, nextState, 'Skalierung')) scaled.push({ object, state: nextState });
  }
  if (scaled.length) emit('object:scaled', { objects: scaled.map(entry => entry.object), scaleFactor: factor, options });
  return scaled.length;
}

export function restoreTransformSnapshot(snapshot = []) {
  let restored = 0;
  for (const entry of snapshot || []) {
    const resolved = resolveSnapshotEntry(entry);
    if (!resolved?.object || !resolved.state) continue;
    if (safeApplyDrawingObjectState(resolved.object, resolved.state, 'Snapshot-Wiederherstellung')) restored += 1;
  }
  if (restored) emit('scene:changed', { reason: 'transform-restored', count: restored });
  return restored;
}
